const getGPUBufferConstructor = () => (globalThis as typeof globalThis & { GPUBuffer?: typeof GPUBuffer }).GPUBuffer;

export function isGPUBuffer(value: unknown): value is GPUBuffer {
    const GPUBufferCtor = getGPUBufferConstructor();
    return Boolean(GPUBufferCtor && value instanceof GPUBufferCtor);
}

export function getTensorGPUBuffer(tensor: unknown): GPUBuffer | undefined {
    if (!tensor) {
        return undefined;
    }
    const candidate = (tensor as any)?.gpuBuffer;
    return isGPUBuffer(candidate) ? candidate : undefined;
}

export async function copyGPUBufferToFloat32(
    device: GPUDevice,
    buffer: GPUBuffer,
    elementCount: number,
    label: string = 'line-drawing-readback',
): Promise<Float32Array> {
    const byteLength = elementCount * Float32Array.BYTES_PER_ELEMENT;
    const readBuffer = device.createBuffer({
        size: byteLength,
        usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
        label,
    });

    const commandEncoder = device.createCommandEncoder();
    commandEncoder.copyBufferToBuffer(buffer, 0, readBuffer, 0, byteLength);
    device.queue.submit([commandEncoder.finish()]);

    await device.queue.onSubmittedWorkDone();
    await readBuffer.mapAsync(GPUMapMode.READ);
    const copy = readBuffer.getMappedRange().slice(0);
    readBuffer.unmap();
    readBuffer.destroy();
    return new Float32Array(copy);
}

export async function readTensorCPUData(tensor: any): Promise<Float32Array> {
    if (!tensor) {
        throw new Error('线稿输出为空，无法生成图像');
    }
    try {
        const direct = tensor.data;
        if (direct) {
            return toFloat32Array(direct);
        }
    } catch {
        // Ignore and fallback to getData
    }
    if (typeof tensor.getData === 'function') {
        const data = await tensor.getData();
        if (data) {
            return toFloat32Array(data);
        }
    }
    throw new Error('无法读取线稿输出数据 (CPU 路径)');
}

export function composeLineDrawingOnCPU(reliferData: Float32Array, width: number, height: number): string {
    const pixelCount = width * height;
    if (reliferData.length < pixelCount) {
        throw new Error('Relifer 输出尺寸与目标画布不匹配');
    }

    const finalImageData = new Uint8ClampedArray(pixelCount * 4);
    for (let i = 0; i < pixelCount; i++) {
        let val = (reliferData[i] + 1) / 2 * 255;
        val = Math.max(0, Math.min(255, val));
        finalImageData[i * 4] = val;
        finalImageData[i * 4 + 1] = val;
        finalImageData[i * 4 + 2] = val;
        finalImageData[i * 4 + 3] = 255;
    }

    const finalCanvas = document.createElement('canvas');
    finalCanvas.width = width;
    finalCanvas.height = height;
    const ctx = finalCanvas.getContext('2d');
    ctx?.putImageData(new ImageData(finalImageData, width, height), 0, 0);
    return finalCanvas.toDataURL();
}

function toFloat32Array(data: ArrayBufferView | ArrayBuffer): Float32Array {
    if (data instanceof Float32Array) {
        return data;
    }
    if (ArrayBuffer.isView(data)) {
        const view = data as ArrayBufferView;
        const slice = view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength);
        return new Float32Array(slice);
    }
    return new Float32Array(data);
}

